/**
 * @file keyboardPerfCfgGcomeAp.ts
 * @description GCOME_AP 键盘性能参数扩展类型（功能变量区 GetFunc 0x05 / SetFunc 0x06）。
 *
 * 契约基础字段见 keyboardPerCfg.ts（KeyboardPerfCfg：usbReportRate/sleepTime）；
 * 本文件定义 GCOME_AP 全量配置扩展（Tick/防抖/Lock mask/Perf_Cfg mask/WASD 切换）。
 * 所有字段均为可选，用于实现部分更新；getAllPerfCfg 返回全字段。
 */

import type { KeyboardPerfCfg } from './keyboardPerCfg'

/** GCOME_AP 报告率 EP 码（芯片原生码，不做 EEPROM 转码；585 系列 EEPROM 码见 USBReportRate）。 */
export type GcomeApReportRate = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7
// 0=16K, 1=8K, 2=4K, 3=2K, 4=1K, 5=500Hz, 6=250Hz, 7=125Hz

/**
 * GCOME_AP 连接模式（字节36）：
 * 0=2.4G 连接、1=蓝牙1通道、2=蓝牙2通道、3=蓝牙3通道、0x0f=有线连接。
 */
export type GcomeApConnectionMode = 0 | 1 | 2 | 3 | 0x0f

/** GCOME_AP 连接模式文案（展示用）。 */
export const GCOME_AP_CONNECTION_MODE_LABELS: Record<GcomeApConnectionMode, string> = {
  0: '2.4G',
  1: '蓝牙1',
  2: '蓝牙2',
  3: '蓝牙3',
  0x0f: '有线',
}

/** WASD ↔ 方向键互换的写后读回确认报告（诊断用）。 */
export interface WasdSwapReport {
  /** 期望互换的键位数（命中映射的纯按键数）。 */
  target: number
  /** 写后读回确认已互换的键位数。 */
  applied: number
  /** 写后读回与预期不符的键位（写入被固件忽略/覆盖或矩阵被重置）。 */
  mismatches: {
    layer: number
    index: number
    expected: number
    actual: number
  }[]
}

/**
 * GCOME_AP 键盘性能配置全量（读：全字段；写：按提供字段部分更新）。
 * 继承契约字段 usbReportRate（GCOME_AP 语义为 EP 码，见 GcomeApReportRate）/ sleepTime（分钟，0=永不休眠）。
 */
export interface GcomeApPerfStatus extends KeyboardPerfCfg {
  /** 报告率 EP 码（0=16K, 1=8K, 2=4K, 3=2K, 4=1K, 5=500Hz, 6=250Hz, 7=125Hz）。 */
  usbReportRate?: GcomeApReportRate
  /** 功能变量区偏移 33：全键无冲。 */
  fullKeyRollover?: boolean
  /** tick 速率：0=无, 1=30tick, 2=60tick。 */
  tickRate?: number
  /** 防抖等级：0-7。 */
  debounceLevel?: number
  /** 锁定 Win 键。 */
  lockWin?: boolean
  /** 锁定 ALT+TAB。 */
  lockAltTab?: boolean
  /** 锁定 ALT+F4。 */
  lockAltF4?: boolean
  /** 触底时始终触发（触底稳定）。 */
  touchBottomTrigger?: boolean
  /** 智能动态校准（自适应动态校准2.0）。 */
  adaptiveCalibration?: boolean
  /** 系统模式（字节1 Bit1）：true=Mac 模式，false=Win 模式。 */
  macMode?: boolean
  /** 连接模式（字节36）：0=2.4G, 1-3=蓝牙1-3通道, 0x0f=有线。 */
  connectionMode?: GcomeApConnectionMode
  /** WASD 与方向键交换（改键层面，以 manifest 布局物理键位判断状态）。 */
  swapWasd?: boolean
  /** 永不休眠标志（休眠时间码值为 0）。 */
  neverSleep?: boolean
}
